import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";

const RightSidebarContext = createContext({});

export const RightSidebarProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [rightSidebar, setRightSidebar] = useState(<h1>Right Sidebar</h1>);
  const [closable, setClosable] = useState(true); // To control the close button visibility, if using page default content, it's not closable.

  /**
   * store a default sidebar content and activationId, so that we can revert to it when the sidebar is closed.
   */
  const [defaultContent, setDefaultContent] = useState(null);
  /**
   * `activationId` is used to recognise equivalent right sidebar. Set it alongsite setRightSidebar()
   */
  const [activationId, setActivationId] = useState(null);
  const [size, setSize] = useState("md");
  const [topOffset, setTopOffset] = useState(0);

  // Memoized functions ??why??
  const location = useLocation();
  const [myPathname, setMyPathname] = useState();

  /*
    A page's default panel the user has closed, kept so the header's reopen button (ReopenDefaultButton) can
    bring it back. Only set for a default registered with `closableDefault` — every other page's default
    stays permanently open, as before.
  */
  const [parkedDefault, setParkedDefaultState] = useState(null);
  // Mirrored in a ref so the memoised setters below can read it without being rebuilt on every change.
  const parkedRef = useRef(null);
  // The startClosed default already parked on this page (see updateRightSidebar); cleared on route change.
  const startedClosedRef = useRef(null);
  const setParkedDefault = useCallback((next) => {
    const value = typeof next === "function" ? next(parkedRef.current) : next;
    parkedRef.current = value;
    setParkedDefaultState(value);
  }, []);

  /**
   * Update the right sidebar content.
   * @param {React.ReactNode} content - The sidebar content
   * @param {boolean} isDefault - Whether to store as the page's default sidebar content, it will be restored when the sidebar is used and closed by other components.
   * @param {string} _activationId - Provide if isDefault=true. The activationId to use for the sidebar.
   * @param {{closableDefault?: boolean, label?: string, startClosed?: boolean}} [options] - `closableDefault`:
   *   the default panel shows a close button, and closing it really closes the tray (the header then offers
   *   `label` to reopen it). Opt-in per page; omitted, a default is not closable, as it always was.
   *   `startClosed` (with `closableDefault`): the FIRST registration on a page parks it closed instead of
   *   showing it, so the page opens with the tray shut and the header's reopen button showing.
   */
  const updateRightSidebar = useCallback(
    (content, isDefault = false, _activationId = null, options = {}) => {
      if (isDefault) {
        const next = {
          content,
          activationId: _activationId,
          closable: !!options.closableDefault,
          label: options.label ?? null,
        };
        setDefaultContent(next);
        // First registration of a startClosed default on this page: park it rather than show it. Keyed on the
        // activation id, so later re-registrations (data refreshes) and a reopen are not closed again.
        if (next.closable && options.startClosed && startedClosedRef.current !== _activationId) {
          startedClosedRef.current = _activationId;
          setParkedDefault(next);
          // Owned by this page, so leaving it clears the parked panel like any other route change.
          setMyPathname(location.pathname);
          return;
        }
        if (parkedRef.current && next.closable) {
          // The user closed this default: pages re-register it whenever their data changes (a save, a
          // refetch), and that must refresh what reopening will show without putting it back on screen.
          setParkedDefault(next);
          return;
        }
      }
      setRightSidebar(content);
      setClosable(!isDefault || !!options.closableDefault); // Default so it's not closable, unless opted in
      if (!isDefault && parkedRef.current) {
        /*
          Something put a step INTO the tray while the page's closable default was closed — e.g. the approve /
          reject confirmation (AnswerActionButtons' ActionSideBar), which sets content without opening it and
          would otherwise be invisible. When continuing depends on the tray, the tray opens itself — and the
          default is un-parked, so once the decision is made the step closes back to Manage, open, exactly as
          if the user had never closed the tray.
        */
        setParkedDefault(null);
        setActivationId(null);
        setIsOpen(true);
        setMyPathname(location.pathname);
      }
    },
    [setParkedDefault, location.pathname]
  );

  /**
   * Close the right sidebar.
   * @type {(function(boolean=): void)|*} - Forced close will not restore the default content and clear it out.
   */
  const doClose = useCallback(
    (forcedArg = false) => {
      // Only a real `true` forces. Buttons wire this straight to onClick ({doClose}), which passes the click
      // EVENT here — truthy — so every close button was silently a forced close.
      const forced = forcedArg === true;
      if (defaultContent?.closable && !forced && activationId === defaultContent.activationId) {
        /*
          Code asked to close while the page's closable default (Manage) is showing — e.g. a Submit/Approve
          tidying up before it sends. Only the USER closes Manage (the tray's ✕, closeByUser), so it stays.
        */
        return;
      } else if (defaultContent?.closable && !forced && parkedRef.current) {
        // Another panel was opened over a closed default: closing it leaves the tray closed, as the user left it.
        setIsOpen(false);
      } else if (defaultContent && !forced) {
        console.log("Rightsidebar fallback to default content");
        setRightSidebar(defaultContent.content);
        setActivationId(defaultContent.activationId);
        setClosable(!!defaultContent.closable);
      } else {
        // clean up
        setIsOpen(false);
        setRightSidebar(null);
        setDefaultContent(null);
        setActivationId(null);
        setClosable(true);
        setParkedDefault(null);
      }
    },
    [defaultContent, activationId, setParkedDefault]
  );

  /**
   * The tray's own close button. On a page whose default is closable (Manage), closing the DEFAULT parks it —
   * the one way it is ever closed — and the header's reopen button appears. Anything else showing (AI, a
   * citation, an approve step) just closes back as doClose does.
   */
  const closeByUser = useCallback(() => {
    if (defaultContent?.closable && activationId === defaultContent.activationId) {
      setParkedDefault(defaultContent);
      setIsOpen(false);
      return;
    }
    doClose();
  }, [defaultContent, activationId, doClose, setParkedDefault]);


  const doOpen = useCallback(
    (id = null) => {
      // A page re-opening its own closed default (its register-and-open effect re-running) stays closed.
      if (id && parkedRef.current?.activationId === id) return;
      console.log("RightSidebar opened with id", id);
      setActivationId(id);
      if (!isOpen) {
        setIsOpen(true);
      }
      setMyPathname(location.pathname);
    },
    [isOpen, location.pathname]
  );

  /** Reopen the page's parked default panel (see `parkedDefault`). */
  const reopenDefault = useCallback(() => {
    if (!parkedDefault) return;
    setRightSidebar(parkedDefault.content);
    setActivationId(parkedDefault.activationId);
    setClosable(true);
    setParkedDefault(null);
    setIsOpen(true);
    setMyPathname(location.pathname);
  }, [parkedDefault, location.pathname, setParkedDefault]);

  // Close the right sidebar when the route changes
  useEffect(() => {
    if (myPathname !== location.pathname) {
      console.log("Sidebar doClose() as route changed to:", location.pathname);
      doClose(true);
      setRightSidebar(null);
      setDefaultContent(null);
      setParkedDefault(null);
      startedClosedRef.current = null;
      setMyPathname(null);
    }
  }, [location.pathname, myPathname, doClose, setParkedDefault]);

  return (
    <RightSidebarContext.Provider
      value={{
        rightSidebar,
        setRightSidebar: updateRightSidebar, // Use memoized setter
        isOpen,
        doOpen, // Memoized
        doClose, // Memoized
        closeByUser, // the tray's ✕ — see above
        size,
        setSize,
        activationId,
        closable,
        topOffset,
        setTopOffset,
        // The closed default panel's label, or null — what the header's reopen button reads.
        reopenableLabel: parkedDefault?.label ?? null,
        reopenDefault,
      }}
    >
      {children}
    </RightSidebarContext.Provider>
  );
};

/**
 * Custom hook to access the right sidebar context.
 */
export const useRightSidebar = () => useContext(RightSidebarContext);
